if (!customElements.get('ia-css-slider')) {
  class IaCssSlider extends HTMLElement {
    constructor() {
      super();
      
      // DOM references
      this.slidesContainer = null;

      // Drag state - single source of truth
      this.dragState = {
        active: false,
        pointerId: null,
        startX: 0,
        startScrollLeft: 0,
        startY: 0,
        hasMoved: false,
        dragThreshold: 5 // pixels to move before starting drag
      };

      // Bound handlers
      this._handlePointerDown = this._handlePointerDown.bind(this);
      this._handlePointerMove = this._handlePointerMove.bind(this);
      this._handlePointerEnd = this._handlePointerEnd.bind(this);
      
      this._isInitialized = false;
    }

    connectedCallback() {
      this.slidesContainer = this.querySelector('.ia-css-slider__slides');
      if (!this.slidesContainer) {
        // console.warn('ia-css-slider: .ia-css-slider__slides container not found');
        return;
      }

      // The arrows work wherever they are shown; dragging stays a mouse thing.
      this._initArrows();

      // Only enable drag on devices with fine pointer (mouse/trackpad)
      if (window.matchMedia('(pointer: fine)').matches) {
        this._initialize();
      }
    }

    disconnectedCallback() {
      this._cleanup();
      this._cleanupArrows();
    }

    /* ---- Arrows -----------------------------------------------------------
       The row scrolls natively, so an arrow is one scrollBy of a slide plus the
       gap. The section owns the buttons and a row reaches them either by name
       (data-controls lists row ids, space separated) or by sitting inside a
       data-ia-slider-arrows element, which is how a set of rows that are never
       on screen together (one per tab) shares a single pair.
       A row that is off screen answers neither the click nor the state sync, so
       the pair only ever acts on the row the visitor is looking at.
       A pair with nothing to page through is hidden; at the ends of a row that
       does scroll the spent button only dims, because a control that vanishes
       mid-interaction is worse than a dim one. */
    _initArrows() {
      const scope =
        (this.id && document.querySelector(`[data-controls~="${this.id}"]`)) ||
        this.closest('[data-ia-slider-arrows]') ||
        this;
      this.prevButton = scope.querySelector('[data-ia-slider-prev]');
      this.nextButton = scope.querySelector('[data-ia-slider-next]');
      if (!this.prevButton && !this.nextButton) return;

      const onScreen = () => this.offsetParent !== null;
      // The pair as ia-slider-arrows renders it. Buttons placed by hand without
      // that wrapper still work, they just never hide themselves.
      const pair = (this.prevButton || this.nextButton).closest('.ia-slider-arrows');
      this._step = (direction) => {
        if (!onScreen()) return;
        const slide = this.slidesContainer.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.slidesContainer).columnGap) || 0;
        const by = slide ? slide.getBoundingClientRect().width + gap : this.clientWidth;
        this.slidesContainer.scrollBy({ left: by * direction, behavior: 'smooth' });
      };
      this._onPrev = () => this._step(-1);
      this._onNext = () => this._step(1);
      this._syncArrows = () => {
        if (!onScreen()) return;
        const el = this.slidesContainer;
        const max = el.scrollWidth - el.clientWidth - 1;
        pair?.classList.toggle('ia-slider-arrows--idle', max <= 0);
        if (this.prevButton) this.prevButton.disabled = el.scrollLeft <= 0;
        if (this.nextButton) this.nextButton.disabled = el.scrollLeft >= max;
      };

      this.prevButton?.addEventListener('click', this._onPrev);
      this.nextButton?.addEventListener('click', this._onNext);
      this.slidesContainer.addEventListener('scroll', this._syncArrows, { passive: true });
      // A row measures zero while its panel is closed and jumps to its real
      // size when the panel opens, so the observer covers both the viewport
      // resize and the moment this row takes the buttons over.
      this._arrowsObserver = new ResizeObserver(() => this._syncArrows());
      this._arrowsObserver.observe(this.slidesContainer);
      this._syncArrows();
    }

    _cleanupArrows() {
      this.prevButton?.removeEventListener('click', this._onPrev);
      this.nextButton?.removeEventListener('click', this._onNext);
      this.slidesContainer?.removeEventListener('scroll', this._syncArrows);
      this._arrowsObserver?.disconnect();
    }

    _initialize() {
      if (this._isInitialized || !this.slidesContainer) return;

      // Get sensitivity from data attribute (default: 1.0)
      // Higher values = more sensitive (e.g., 2.0 = twice as far)
      // Lower values = less sensitive (e.g., 0.5 = half as far)
      this.sensitivity = parseFloat(this.dataset.sensitivity || '1.0');

      // Prevent native touch scrolling on the container
      this.slidesContainer.style.touchAction = 'pan-y';
      this.slidesContainer.style.cursor = 'grab';

      // Use pointer events for better cross-device support
      this.slidesContainer.addEventListener('pointerdown', this._handlePointerDown, { passive: true });
      
      this._isInitialized = true;
    }

    _cleanup() {
      if (!this._isInitialized) return;

      // End any active drag
      this._endDrag();

      // Remove all event listeners
      if (this.slidesContainer) {
        this.slidesContainer.removeEventListener('pointerdown', this._handlePointerDown);
        this.slidesContainer.removeEventListener('pointermove', this._handlePointerMove);
        this.slidesContainer.removeEventListener('pointerup', this._handlePointerEnd);
        this.slidesContainer.removeEventListener('pointercancel', this._handlePointerEnd);
        
        // Reset cursor
        this.slidesContainer.style.cursor = '';
      }

      // Remove document-level listeners (safety net)
      document.removeEventListener('pointermove', this._handlePointerMove);
      document.removeEventListener('pointerup', this._handlePointerEnd);
      document.removeEventListener('pointercancel', this._handlePointerEnd);

      this._isInitialized = false;
    }

    _handlePointerDown(e) {
      // Only respond to primary button (left mouse button)
      if (e.button !== 0) return;
    
      // Prevent starting a new drag if one is already active
      if (this.dragState.active) {
        return;
      }
    
      // DON'T capture the pointer yet - only capture if we actually start dragging
      // Removing setPointerCapture from here
    
      // Initialize drag state (but don't mark as active yet - wait for movement)
      this.dragState = {
        active: false,
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        startScrollLeft: this.slidesContainer.scrollLeft,
        hasMoved: false,
        dragThreshold: this.dragState.dragThreshold || 5
      };
    
      // Add move and end listeners
      this.slidesContainer.addEventListener('pointermove', this._handlePointerMove, { passive: false });
      this.slidesContainer.addEventListener('pointerup', this._handlePointerEnd);
      this.slidesContainer.addEventListener('pointercancel', this._handlePointerEnd);
      
      // Fallback document listeners
      document.addEventListener('pointermove', this._handlePointerMove, { passive: false });
      document.addEventListener('pointerup', this._handlePointerEnd);
      document.addEventListener('pointercancel', this._handlePointerEnd);
    }
    
    _handlePointerMove(e) {
      // Ignore if this isn't our pointer
      if (e.pointerId !== this.dragState.pointerId) {
        return;
      }
    
      // Calculate movement distance
      const deltaX = Math.abs(e.clientX - this.dragState.startX);
      const deltaY = Math.abs(e.clientY - this.dragState.startY);
    
      // Check if we've exceeded the drag threshold
      if (!this.dragState.active) {
        const totalMovement = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        if (totalMovement >= this.dragState.dragThreshold) {
          // NOW capture the pointer when we actually start dragging
          try {
            this.slidesContainer.setPointerCapture(e.pointerId);
          } catch (err) {
            // console.warn('ia-css-slider: pointer capture failed');
          }
          
          // Start dragging
          this.dragState.active = true;
          this.dragState.hasMoved = true;
          this._applyDragStyles();
          e.preventDefault();
        } else {
          // Haven't moved enough yet
          return;
        }
      } else {
        // We're actively dragging - prevent default
        e.preventDefault();
      }
    
      // Update scroll position
      const scrollDeltaX = (e.clientX - this.dragState.startX) * this.sensitivity;
      if (this.slidesContainer) {
        this.slidesContainer.scrollLeft = this.dragState.startScrollLeft - scrollDeltaX;
      }
    }
    
    _handlePointerEnd(e) {
      // Ignore if this isn't our pointer
      if (e.pointerId !== this.dragState.pointerId) {
        return;
      }
    
      // If we dragged, prevent the click event from firing
      if (this.dragState.active) {
        // Add a one-time click handler to prevent the click
        const preventClick = (clickEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
          clickEvent.stopImmediatePropagation();
        };
        
        this.slidesContainer.addEventListener('click', preventClick, { 
          capture: true, 
          once: true 
        });
        
        this._endDrag();
      } else {
        // User just clicked without dragging - clean up listeners but allow click
        this._cleanupListeners();
        this.dragState = {
          active: false,
          pointerId: null,
          startX: 0,
          startY: 0,
          startScrollLeft: 0,
          hasMoved: false,
          dragThreshold: 5
        };
      }
    }

    _cleanupListeners() {
      // Release pointer capture
      if (this.dragState.pointerId !== null && this.slidesContainer) {
        try {
          this.slidesContainer.releasePointerCapture(this.dragState.pointerId);
        } catch (err) {
          // Already released or not supported
        }
      }

      // Remove event listeners
      if (this.slidesContainer) {
        this.slidesContainer.removeEventListener('pointermove', this._handlePointerMove);
        this.slidesContainer.removeEventListener('pointerup', this._handlePointerEnd);
        this.slidesContainer.removeEventListener('pointercancel', this._handlePointerEnd);
      }
      
      // Remove document listeners
      document.removeEventListener('pointermove', this._handlePointerMove);
      document.removeEventListener('pointerup', this._handlePointerEnd);
      document.removeEventListener('pointercancel', this._handlePointerEnd);
    }

    _endDrag() {
      if (!this.dragState.active) return;

      // Clean up listeners
      this._cleanupListeners();

      // Remove dragging styles
      this._removeDragStyles();

      // Reset drag state
      this.dragState = {
        active: false,
        pointerId: null,
        startX: 0,
        startY: 0,
        startScrollLeft: 0,
        hasMoved: false,
        dragThreshold: 5
      };
    }

    _applyDragStyles() {
      this.classList.add('is-dragging');
      
      if (this.slidesContainer) {
        this.slidesContainer.style.cursor = 'grabbing';
        this.slidesContainer.style.userSelect = 'none';
      }
      
      // Prevent text selection globally while dragging
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';
    }

    _removeDragStyles() {
      this.classList.remove('is-dragging');
      
      if (this.slidesContainer) {
        this.slidesContainer.style.cursor = 'grab';
        this.slidesContainer.style.userSelect = '';
      }
      
      // Restore global styles
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    }

    // Public API: programmatically end drag if needed
    cancelDrag() {
      this._endDrag();
    }

    // Public API: check if currently dragging
    get isDragging() {
      return this.dragState.active;
    }
  }

  customElements.define('ia-css-slider', IaCssSlider);
}
